import React, { useState, useEffect } from 'react';
import TransactionFilters from '../components/TransactionFilters';
import useChargingSessions from '../hooks/useChargingSessions';
import { toast } from 'react-hot-toast';
import { formatINR } from "../utils/formatINR";

const ChargingSessions = () => {
  const { sessions, isLoading, error, message } = useChargingSessions();
  const [localSessions, setLocalSessions] = useState([]);

  useEffect(() => {
    if (message) {
      console.log("Server message:", message);
      toast.success(message);
    }
  }, [message]);

  // normalize / clean sessions
  useEffect(() => {
    if (sessions) {
      const cleaned = sessions.map(s => ({
        ...s,
        user_id: s.user_id?.Valid ? s.user_id.String : "",
      }));
      setLocalSessions(cleaned);
    }
  }, [sessions]);

  // filters state
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateRange: '30'
  });

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      dateRange: '30'
    });
  };

  // apply filters
  const filteredSessions = localSessions.filter(session => {
    if (filters.search && !session.id.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }

    if (filters.status !== 'all') {
      // infer status from cost or duration — adjust if you have explicit status
      const isCompleted = session.session_end && session.total_cost >= 0;
      if (filters.status === 'Completed' && !isCompleted) return false;
      if (filters.status === 'Ongoing' && isCompleted) return false;
    }

    const sessionDate = new Date(session.session_start);
    const today = new Date();

    if (filters.dateRange === 'today') {
      const isToday =
        sessionDate.getDate() === today.getDate() &&
        sessionDate.getMonth() === today.getMonth() &&
        sessionDate.getFullYear() === today.getFullYear();
      if (!isToday) return false;
    } else if (filters.dateRange === 'custom') {
      if (filters.startDate && sessionDate < new Date(filters.startDate)) {
        return false;
      }
      if (filters.endDate && sessionDate > new Date(filters.endDate)) {
        return false;
      }
    } else if (filters.dateRange !== 'all') {
      const days = parseInt(filters.dateRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      if (sessionDate < cutoffDate) {
        return false;
      }
    }

    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-2 sm:px-2 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">Charging Sessions</h1>
            <div className="flex items-center space-x-4">
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                Export Data
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-fullxl mx-auto px-2 sm:px-0 lg:px-0 py-4">
        <TransactionFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Recent Charging Sessions</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Station ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gun ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Energy (kWh)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SOC (Start → End)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration (min)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Time</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSessions.map((s, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.vehicle_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.station_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.gun_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.energy_kwh}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.start_soc} → {s.end_soc}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Math.round(s.duration_seconds/60)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(s.total_cost)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.session_start}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.session_end}</td>
                  </tr>
                ))}

                {filteredSessions.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan="11" className="px-6 py-4 text-center text-sm text-gray-500">
                      No charging sessions found
                    </td>
                  </tr>
                )}

                {isLoading && (
                  <tr>
                    <td colSpan="11" className="px-6 py-4 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                )}

                {/* {error && (
                  <tr>
                    <td colSpan="11" className="px-6 py-4 text-center text-sm text-red-500">
                      {error}
                    </td>
                  </tr>
                )} */}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChargingSessions;
